import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { Skeleton } from '@/components/ui/Skeleton'
import { useBrandTemplates, useUpdateTemplate } from '@/lib/settings-api'
import type { BrandTemplate } from '@/lib/settings-types'

export function TemplatesCard({ canEdit }: { canEdit: boolean }) {
  const templatesQuery = useBrandTemplates()
  const [editing, setEditing] = useState<BrandTemplate | null>(null)

  return (
    <Card>
      <CardHeader title="Huisstijlen" subtitle={canEdit ? 'Afzender-mailbox bewerkbaar' : 'Alleen-lezen'} />
      {templatesQuery.isError ? (
        <ErrorState compact error={templatesQuery.error} onRetry={() => templatesQuery.refetch()} />
      ) : templatesQuery.isLoading ? (
        <Skeleton className="mt-4 h-24 w-full" />
      ) : (
        <div className="mt-4 space-y-2">
          {(templatesQuery.data ?? []).map((template) => (
            <div key={template.code} className="flex items-center justify-between gap-3 rounded-lg border border-hairline px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="h-6 w-6 rounded-md" style={{ backgroundColor: template.primaryColor }} />
                <div>
                  <p className="text-sm font-medium text-ink">{template.displayName}</p>
                  <p className="text-xs text-ink-faint">{template.senderMailbox}</p>
                </div>
              </div>
              {canEdit ? (
                <button type="button" className="btn-ghost h-8 w-8 shrink-0 p-0" onClick={() => setEditing(template)} aria-label="Afzender bewerken">
                  <Pencil className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {editing ? <TemplateModal template={editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  )
}

function TemplateModal({ template, onClose }: { template: BrandTemplate; onClose: () => void }) {
  const update = useUpdateTemplate()
  const [senderMailbox, setSenderMailbox] = useState(template.senderMailbox)

  function submit() {
    if (!senderMailbox.trim()) return
    update.mutate({ code: template.id ?? template.code, input: { senderMailbox: senderMailbox.trim() } }, { onSuccess: onClose })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Afzender ${template.displayName}`}
      description="De mailbox waaruit facturen voor deze huisstijl worden verstuurd."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!senderMailbox.trim() || update.isPending}>Opslaan</button>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-soft">Afzender-mailbox</span>
        <input value={senderMailbox} onChange={(e) => setSenderMailbox(e.target.value)} className="field" type="email" autoFocus />
      </label>
    </Modal>
  )
}
